import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { IDiscussionGuideQuestion } from '../../models/data-capture.models';
import { FormGroupQuestion } from './question-node/question-node.component';

export type FormGroupTheme = FormGroup<{
  theme: FormControl<string>;
  questions: FormArray<FormGroupQuestion>;
}>;

export type FormOverall = FormGroup<{
  themes: FormArray<FormGroupTheme>;
}>;

@Component({
  selector: 'app-reorderable-expansion-panels',
  templateUrl: './re-orderable-theme.component.html',
  styleUrls: ['./re-orderable-theme.component.scss'],
  standalone: false,
})
export class ReorderableExpansionPanelsComponent implements OnInit {
  @Input() showErrors = false;
  @Input() suggestedQuestionsByTheme: Record<
    string,
    IDiscussionGuideQuestion[]
  > = {};

  @Output() approveSuggestion = new EventEmitter<string>();
  @Output() declineSuggestion = new EventEmitter<string>();

  formGroup: FormOverall;
  collapsedStates: boolean[] = [];

  get themes() {
    return this.formGroup.controls.themes;
  }

  getSuggestedQuestions(themeName: string): IDiscussionGuideQuestion[] {
    return this.suggestedQuestionsByTheme?.[themeName] || [];
  }

  constructor(private formGroupDirective: FormGroupDirective) {}

  ngOnInit() {
    this.formGroup = this.formGroupDirective.form.controls
      .reorderablePanel as FormOverall;

    this.collapsedStates = this.themes.controls.map(() => false);
  }

  addTheme() {
    this.themes.push(
      new FormGroup({
        theme: new FormControl<string>('', Validators.required),
        questions: new FormArray<FormGroupQuestion>([]),
      })
    );

    this.collapsedStates.push(false);
  }

  cloneTheme(themeIndex: number) {
    const themeGroup = this.themes.at(themeIndex);
    const baseThemeName = themeGroup.controls.theme.value || '';
    const cloneThemeName = this.createCloneThemeName(baseThemeName);

    this.themes.insert(
      themeIndex + 1,
      new FormGroup({
        theme: new FormControl<string>(cloneThemeName, Validators.required),
        questions: new FormArray<FormGroupQuestion>(
          themeGroup.controls.questions.controls.map((question) =>
            this.cloneQuestion(question, cloneThemeName, null)
          )
        ),
      })
    );

    this.collapsedStates.splice(themeIndex + 1, 0, false);
  }

  addQuestion(themeIndex: number, parent?: FormGroupQuestion, index?: number) {
    const themeGroup = this.themes.at(themeIndex);
    const theme = themeGroup.controls.theme.value;

    const newQuestion = new FormGroup({
      question_id: new FormControl<string>(
        crypto.randomUUID(),
        Validators.required
      ),
      question: new FormControl<string>('', Validators.required),
      theme: new FormControl<string>(theme, Validators.required),
      label: new FormControl<string>(''),
      parent_question_id: new FormControl<string | null>(
        parent ? parent.controls.question_id.value : null
      ),
      children: new FormArray<FormGroupQuestion>([]),
    });

    if (parent) {
      if (index === undefined) {
        parent.controls.children.push(newQuestion);
      } else {
        parent.controls.children.insert(index, newQuestion);
      }
    } else {
      if (index === undefined) {
        themeGroup.controls.questions.push(newQuestion);
      } else {
        themeGroup.controls.questions.insert(index, newQuestion);
      }
    }

    setTimeout(() => {
      const element = document.getElementById(
        this.toQuestionInputId(newQuestion.controls.question_id.value)
      );

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    });
  }

  removeQuestion(themeIndex: number, question: FormGroupQuestion) {
    const themeQuestions = this.getQuestions(themeIndex);
    this.removeRecursively(themeQuestions, question);
  }

  removeTheme(themeIndex: number) {
    this.themes.removeAt(themeIndex);
    this.collapsedStates.splice(themeIndex, 1);
  }

  onThemeDrop(event: CdkDragDrop<FormGroup[]>) {
    if (event.previousIndex !== event.currentIndex) {
      const movedTheme = this.themes.at(event.previousIndex);
      this.themes.removeAt(event.previousIndex);
      this.themes.insert(event.currentIndex, movedTheme);
    }
  }

  onQuestionDrop(themeIndex: number, event: CdkDragDrop<FormGroup[]>) {
    if (event.previousIndex !== event.currentIndex) {
      const questions = this.getQuestions(themeIndex);
      const movedQuestion = questions.at(event.previousIndex);
      questions.removeAt(event.previousIndex);
      questions.insert(event.currentIndex, movedQuestion);
    }
  }

  toggleCollapse(index: number) {
    this.collapsedStates[index] = !this.collapsedStates[index];
  }

  shouldHighlightThemeCard(themeGroup: FormGroupTheme) {
    if (!this.showErrors) {
      return false;
    }

    return themeGroup.hasError('noQuestions');
  }

  getThemeOffset(themeIndex: number): number {
    return this.themes.controls
      .slice(0, themeIndex)
      .reduce(
        (acc, theme) => acc + theme.controls.questions.controls.length,
        0
      );
  }

  shouldHighlightThemeName(themeGroup: FormGroupTheme) {
    if (!this.showErrors) {
      return false;
    }

    const themeControl = themeGroup.controls.theme;

    return (
      themeControl.hasError('noThemeName') ||
      themeControl.hasError('duplicateThemeNames') ||
      (themeControl.invalid && (themeControl.dirty || themeControl.touched))
    );
  }

  private getQuestions(themeIndex: number) {
    return this.themes.at(themeIndex).controls.questions;
  }

  private cloneQuestion(
    question: FormGroupQuestion,
    themeName: string,
    parentQuestionId: string | null
  ): FormGroupQuestion {
    const questionId = crypto.randomUUID();

    const clonedQuestion = new FormGroup({
      question_id: new FormControl<string>(questionId, Validators.required),
      question: new FormControl<string>(
        question.controls.question.value,
        Validators.required
      ),
      theme: new FormControl<string>(themeName, Validators.required),
      label: new FormControl<string>(question.controls.label.value || ''),
      parent_question_id: new FormControl<string | null>(parentQuestionId),
      children: new FormArray<FormGroupQuestion>(
        question.controls.children.controls.map((child) =>
          this.cloneQuestion(child, themeName, questionId)
        )
      ),
    });

    return clonedQuestion;
  }

  private createCloneThemeName(originalThemeName: string) {
    return `${originalThemeName.trim() || 'Untitled section'} (copy)`;
  }

  private toQuestionInputId(questionId: string) {
    return `question-input-${questionId}`;
  }

  private removeRecursively(
    array: FormArray<FormGroupQuestion>,
    target: FormGroupQuestion
  ) {
    const index = array.controls.indexOf(target);
    if (index > -1) {
      array.removeAt(index);
      return true;
    }

    for (const group of array.controls) {
      const children = group.controls.children;
      if (this.removeRecursively(children, target)) {
        return true;
      }
    }

    return false;
  }
}
